import React, { useState, useEffect } from 'react';
import { useCustomerMeetings } from '../hooks/useMeetings';
import { useAuth } from '../../../contexts/AuthContext';
import { hamtaBestallarnamn } from '../../../utils/nameUtils';
import {
  getMeetingDisplayName,
  formatScheduledAt,
  formatDuration,
  formatMeetingDateTime,
  
} from '../utils/meetingTemplates';
import axios from 'axios';
import api from '../../../services/api';
import { ProtocolBuilder } from './protocol/ProtocolBuilder';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Ikoner
const DocumentTextIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const ChevronDownIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
  </svg>
);

const MapPinIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

/**
 * Sektion för kunder att se och svara på möten i myProjects
 */
const CustomerMeetingsSection = ({ customerId, projectId, project: projectProp }) => {
  const { meetings, loading,  refetch } = useCustomerMeetings(customerId);
  const { user } = useAuth();

  const [expandedMeetings, setExpandedMeetings] = useState({}); // { [meetingId]: boolean }
  const [respondingTo, setRespondingTo] = useState(null); // meetingId som kunden svarar på
  const [responses, setResponses] = useState({}); // { [proposalIndex]: boolean }
  const [submitting, setSubmitting] = useState(false);
  const [protocols, setProtocols] = useState([]);
  const [protocolModalMeeting, setProtocolModalMeeting] = useState(null);
  // Hemläxa: `homeworkUploaded` = inskickade filnamn per möte. `valdaFiler` =
  // filer kunden valt men inte skickat än; de laddas upp först vid "Skicka
  // hemläxa", så en fil man ångrar aldrig hamnar i SharePoint.
  const [homeworkUploading, setHomeworkUploading] = useState(null); // { meetingId, klara, totalt }
  const [homeworkUploaded, setHomeworkUploaded] = useState({}); // { [meetingId]: string[] }
  const [valdaFiler, setValdaFiler] = useState({}); // { [meetingId]: File[] }
  const [kompletterar, setKompletterar] = useState({}); // { [meetingId]: boolean }
  const [visaAllaTidigare, setVisaAllaTidigare] = useState(false);

  // Initiera homeworkUploaded från mötesdata (så det visas efter refresh).
  // Möten från före flerfilsstödet har bara homeworkFileName.
  useEffect(() => {
    if (!meetings?.length) return;
    const uploaded = {};
    meetings.forEach(m => {
      if (m.type !== 'hemlaxa') return;
      const namn = m.homeworkFiles?.length
        ? m.homeworkFiles.map(f => f.fileName)
        : (m.homeworkFileName ? [m.homeworkFileName] : []);
      if (namn.length > 0) uploaded[m._id] = namn;
    });
    if (Object.keys(uploaded).length > 0) {
      setHomeworkUploaded(prev => ({ ...prev, ...uploaded }));
    }
  }, [meetings]);

  // Toggle expand/collapse för ett möte
  const toggleMeetingExpanded = (meetingId) => {
    setExpandedMeetings(prev => ({
      ...prev,
      [meetingId]: !prev[meetingId]
    }));
  };

  // Hämta protokoll för projektet
  useEffect(() => {
    const fetchProtocols = async () => {
      if (!projectId) return;
      try {
        const res = await api.get(`/meeting-protocols/project/${projectId}`);
        setProtocols(res.data.data || []);
      } catch (err) {
        console.error('Kunde inte hämta protokoll:', err);
      }
    };
    fetchProtocols();
  }, [projectId]);

  // Hitta protokoll för ett möte
  const getProtocolForMeeting = (meetingId) => {
    return protocols.find(p => p.meetingId?._id === meetingId || p.meetingId === meetingId);
  };


  // Filtrera möten för det valda projektet om angivet
  const filteredMeetings = projectId
    ? meetings.filter(m => {
        const meetingProjectId = m.project?._id || m.project;
        return meetingProjectId?.toString() === projectId?.toString();
      })
    : meetings;

  // Den inloggade kundens deltagarpost. Matcha strikt på userId först —
  // `userId === customerId || role === 'customer'` i samma find gav FÖRSTA
  // kunden i listan, alltså huvudkunden. Beställare 2 fick då huvudkundens
  // (tomma) svar, och mötet låg kvar under "Väntar på ditt svar" fast hans svar
  // sparats. Han svarade tre gånger och trodde att det inte gick.
  // Fallback till första kunden bara när ingen deltagare har kundens userId.
  const getCustomerAttendee = (meeting) => {
    const customerIdStr = String(customerId);
    return meeting.attendees?.find(a => {
      const aId = a.userId?._id || a.userId;
      return aId && String(aId) === customerIdStr;
    }) || meeting.attendees?.find(a => a.role === 'customer');
  };



  const hasCustomerResponded = (meeting) => {
    const attendee = getCustomerAttendee(meeting);
    return attendee?.proposalResponses?.length > 0;
  };

  // Gruppera möten
  const allProposedMeetings = filteredMeetings.filter(m =>
    m.status === 'proposed' && m.proposalWorkflow?.proposals?.length > 0
  );

  // Dela upp i "behöver svara" och "har svarat"
  const pendingProposals = allProposedMeetings.filter(m => !hasCustomerResponded(m));
  const respondedProposals = allProposedMeetings.filter(m => hasCustomerResponded(m));

  const upcomingMeetings = filteredMeetings.filter(m =>
    m.status === 'scheduled' && new Date(m.scheduledAt) >= new Date()
  );
  const pastMeetings = filteredMeetings.filter(m =>
    m.status === 'completed' || (m.status === 'scheduled' && new Date(m.scheduledAt) < new Date())
  );

  // En hemläxa som saknar fil är "aktuell" fram till en vecka efter mötet. Inom
  // fönstret flaggas den i rubriken och kortet garanteras en plats i listan —
  // glömde kunden ladda upp och IA ber om filen på mötet ska den vara lätt att
  // hitta. Efter fönstret slutar vi flagga: ibland blir det ingen hemläxa, och
  // då ska mötet inte ligga och lysa för evigt. Själva uppladdningsknappen
  // finns kvar på kortet oavsett ålder — den försvinner bara när filen finns.
  const HEMLAXA_PAMINNELSE_DAGAR = 7;
  const hemlaxaArAktuell = (m) => {
    if (!m.scheduledAt) return true; // bara föreslaget — mötet har inte hållits
    const dagarSedan = (Date.now() - new Date(m.scheduledAt).getTime()) / 86400000;
    return dagarSedan <= HEMLAXA_PAMINNELSE_DAGAR;
  };
  const saknarAktuellHemlaxa = (m) =>
    m.type === 'hemlaxa' && !homeworkUploaded[m._id] && hemlaxaArAktuell(m);

  // Backend sorterar stigande på scheduledAt (meetingController:480), så
  // pastMeetings är ÄLDST först. Visningen vändes: listan visade de fem äldsta
  // mötena och dolde de senaste, vilket är precis bakvänt för "Tidigare möten".
  const tidigareNyastForst = [...pastMeetings].reverse();
  const tidigareAttVisa = visaAllaTidigare
    ? tidigareNyastForst
    : [
      ...tidigareNyastForst.slice(0, 5),
      ...tidigareNyastForst.slice(5).filter(saknarAktuellHemlaxa)
    ];
  const finnsDoldaTidigare = pastMeetings.length > tidigareAttVisa.length;
  const antalSaknadHemlaxa = pastMeetings.filter(saknarAktuellHemlaxa).length;

  // Hantera svar på förslag
  const handleResponseChange = (proposalIndex, canAttend) => {
    setResponses(prev => ({
      ...prev,
      [proposalIndex]: canAttend
    }));
  };

  // Skicka svar
  const handleSubmitResponse = async (meeting) => {
    const responseArray = Object.entries(responses).map(([idx, canAttend]) => ({
      proposalIndex: parseInt(idx),
      canAttend
    }));

    // Kolla att alla förslag har svar
    if (responseArray.length !== meeting.proposalWorkflow.proposals.length) {
      alert('Vänligen markera alla föreslagna tider');
      return;
    }

    try {
      setSubmitting(true);

      const attendee = getCustomerAttendee(meeting);

      if (!attendee?.responseToken) {
        alert('Kunde inte hitta svarstoken');
        return;
      }

      await axios.post(`${API_URL}/api/meetings/${meeting._id}/respond`, {
        token: attendee.responseToken,
        responses: responseArray
      });

      setRespondingTo(null);
      setResponses({});
      refetch();
    } catch (err) {
      console.error('Fel vid svar:', err);
      alert(err.response?.data?.message || 'Kunde inte skicka svar');
    } finally {
      setSubmitting(false);
    }
  };

  // Bygg SharePoint-sökväg för hemläxa-uppladdning
  const getHomeworkUploadPath = () => {
    if (!projectProp || !user) return null;
    const projectYear = projectProp.projectYear || new Date(projectProp.createdAt).getFullYear().toString();
    // Mappen heter efter avtalsparten, inte efter den som är inloggad. För en
    // BRF är det föreningen — kontaktpersonens namn ger en sökväg som inte finns.
    // Projektets kund går före user: user saknar kundtyp om token är gammal.
    const folderName = `${hamtaBestallarnamn(projectProp.customer || user)} (#${projectProp.projectId})`;
    return `General/Leadme/- Ja/${projectYear}/${folderName}/Underlag/Underlag-delad/IA Hemläxa`;
  };

  // Lägg valda filer i listan. Samma namn två gånger ersätter den första —
  // SharePoint hade ändå skrivit över den.
  const laggTillValdaFiler = (meetingId, filer) => {
    if (!filer.length) return;
    setValdaFiler(prev => {
      const befintliga = (prev[meetingId] || []).filter(f => !filer.some(n => n.name === f.name));
      return { ...prev, [meetingId]: [...befintliga, ...filer] };
    });
  };

  const taBortValdFil = (meetingId, namn) => {
    setValdaFiler(prev => ({
      ...prev,
      [meetingId]: (prev[meetingId] || []).filter(f => f.name !== namn)
    }));
  };

  // Skicka hemläxa: ladda upp filerna en i taget via /underlag/upload (tyst),
  // och markera sedan mötet med en samlad notis till IA. Går en fil fel ligger
  // de andra redan i SharePoint — de rapporteras ändå, och den misslyckade
  // ligger kvar i listan så kunden kan försöka igen.
  const handleSkickaHemlaxa = async (meetingId) => {
    const filer = valdaFiler[meetingId] || [];
    if (!filer.length) return;
    const folderPath = getHomeworkUploadPath();
    if (!folderPath) {
      alert('Kunde inte bestämma uppladdningsmapp. Försök igen.');
      return;
    }

    const uppladdade = [];
    const misslyckade = [];
    const token = localStorage.getItem('token');
    setHomeworkUploading({ meetingId, klara: 0, totalt: filer.length });

    try {
      for (const file of filer) {
        try {
          const fd = new FormData();
          fd.append('file', file);
          fd.append('folderPath', folderPath);
          fd.append('tyst', 'true');

          // Ladda upp filen till SharePoint via express-fileupload endpoint
          const resp = await fetch(`${API_URL}/underlag/upload`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Accept': 'application/json'
            },
            body: fd
          });
          if (!resp.ok) {
            const errData = await resp.json().catch(() => ({}));
            throw new Error(errData.error || 'Uppladdning misslyckades');
          }
          const data = await resp.json().catch(() => ({}));
          uppladdade.push({ file, namn: data.fileName || file.name });
        } catch (err) {
          console.error(`Fel vid uppladdning av ${file.name}:`, err);
          misslyckade.push({ file, fel: err.message });
        }
        setHomeworkUploading(prev => prev && { ...prev, klara: prev.klara + 1 });
      }

      if (uppladdade.length > 0) {
        const res = await api.post(`/meetings/${meetingId}/homework-uploaded`, {
          homeworkFiles: uppladdade.map(u => u.namn)
        });
        const allaNamn = res.data?.data?.homeworkFiles?.map(f => f.fileName)
          || uppladdade.map(u => u.namn);
        setHomeworkUploaded(prev => ({ ...prev, [meetingId]: allaNamn }));
      }

      setValdaFiler(prev => ({ ...prev, [meetingId]: misslyckade.map(m => m.file) }));
      if (misslyckade.length === 0) {
        setKompletterar(prev => ({ ...prev, [meetingId]: false }));
      } else {
        alert(
          `Kunde inte ladda upp:\n${misslyckade.map(m => `• ${m.file.name}: ${m.fel}`).join('\n')}` +
          (uppladdade.length ? `\n\nÖvriga filer skickades.` : '')
        );
      }
    } catch (err) {
      console.error('Fel vid inskick av hemläxa:', err);
      alert('Kunde inte skicka hemläxan. Försök igen.');
    } finally {
      setHomeworkUploading(null);
    }
  };

  // Hemläxa-statuschip. Samma text i tidsförslag, svarade förslag och bokade
  // möten så kunden ser läget utan att expandera kortet.
  const renderHemlaxaChip = (meeting) => {
    if (meeting.type !== 'hemlaxa') return null;
    if (homeworkUploaded[meeting._id]) {
      return <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded">Hemläxa uppladdad</span>;
    }
    // Slutar flagga en vecka efter mötet. Knappen finns kvar, men ett gammalt
    // möte utan hemläxa ska inte stå och lysa orange i all evighet.
    if (!hemlaxaArAktuell(meeting)) return null;
    return <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-medium rounded">Hemläxa saknas</span>;
  };

  // Hemläxa-uppladdning. Knappen renderas i ALLA tre lägen — tidsförslag,
  // svarade förslag och bokat möte — eftersom mailet med tidsförslagen ber
  // kunden ladda upp hemläxan redan då ("under mötessektionen"), och förslaget
  // kan ligga ute i dagar. Låg knappen bara på bokade möten pekade mailet på
  // något som inte fanns ännu. Backend kräver bara type='hemlaxa', ingen status.
  //
  // Flera filer: kunden väljer filer (flera åt gången eller i omgångar), ser
  // listan, och skickar allt med "Skicka hemläxa". Efter inskick visas filerna
  // och "Lägg till fler filer" öppnar samma väljare för en komplettering.
  const renderHemlaxaUppladdning = (meeting, { visaHjalptext = false } = {}) => {
    if (meeting.type !== 'hemlaxa') return null;
    const id = meeting._id;
    const inskickade = homeworkUploaded[id] || [];
    const valda = valdaFiler[id] || [];
    const laddarUpp = homeworkUploading?.meetingId === id;
    const annanLaddarUpp = homeworkUploading && !laddarUpp;
    const visaValjare = inskickade.length === 0 || kompletterar[id];

    return (
      <div className="space-y-2">
        {inskickade.length > 0 && (
          <div className="text-sm text-green-700 bg-green-50 rounded-lg px-3 py-2">
            <div className="flex items-center gap-2 font-medium">
              <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              <span>Hemläxa inskickad{inskickade.length > 1 ? ` (${inskickade.length} filer)` : ''}</span>
            </div>
            <ul className="mt-1 ml-6 space-y-0.5">
              {inskickade.map(namn => (
                <li key={namn} className="min-w-0 break-words">{namn}</li>
              ))}
            </ul>
            {!visaValjare && (
              <button
                type="button"
                onClick={() => setKompletterar(prev => ({ ...prev, [id]: true }))}
                className="mt-2 ml-6 text-xs text-primary hover:underline"
              >
                + Lägg till fler filer
              </button>
            )}
          </div>
        )}

        {visaValjare && (
          <div className="space-y-2">
            {visaHjalptext && inskickade.length === 0 && (
              <p className="text-sm text-gray-600">
                Du kan ladda upp hemläxan redan nu — du behöver inte vänta på att tiden bokas.
                Välj en eller flera filer och tryck sedan på Skicka hemläxa.
              </p>
            )}

            {valda.length > 0 && (
              <ul className="space-y-1">
                {valda.map(f => (
                  <li key={f.name} className="flex items-center justify-between gap-2 text-sm bg-white border rounded-lg px-3 py-1.5">
                    <span className="min-w-0 break-words text-gray-700">{f.name}</span>
                    {!laddarUpp && (
                      <button
                        type="button"
                        onClick={() => taBortValdFil(id, f.name)}
                        className="flex-shrink-0 text-gray-400 hover:text-red-500"
                        title="Ta bort"
                        aria-label={`Ta bort ${f.name}`}
                      >
                        ✕
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <label className={`cursor-pointer inline-flex items-center gap-2 px-3 py-2 border border-dashed border-primary/40 rounded-lg hover:bg-primary/5 transition-colors text-sm text-primary ${homeworkUploading ? 'opacity-50 pointer-events-none' : ''}`}>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
                <span>{valda.length > 0 ? 'Välj fler filer' : 'Välj filer'}</span>
                <input
                  type="file"
                  multiple
                  className="hidden"
                  accept=".pdf,.pptx,.ppt,.docx,.doc,.jpg,.jpeg,.png,.gif,.webp"
                  onChange={(e) => {
                    laggTillValdaFiler(id, Array.from(e.target.files || []));
                    e.target.value = '';
                  }}
                />
              </label>

              {valda.length > 0 && (
                <button
                  type="button"
                  onClick={() => handleSkickaHemlaxa(id)}
                  disabled={!!homeworkUploading}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  {laddarUpp
                    ? `Laddar upp ${Math.min(homeworkUploading.klara + 1, homeworkUploading.totalt)} av ${homeworkUploading.totalt}...`
                    : `Skicka hemläxa${valda.length > 1 ? ` (${valda.length} filer)` : ''}`}
                </button>
              )}

              {inskickade.length > 0 && !laddarUpp && (
                <button
                  type="button"
                  onClick={() => {
                    setKompletterar(prev => ({ ...prev, [id]: false }));
                    setValdaFiler(prev => ({ ...prev, [id]: [] }));
                  }}
                  className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800"
                >
                  Avbryt
                </button>
              )}
            </div>
            {annanLaddarUpp && (
              <p className="text-xs text-gray-500">Väntar på en annan uppladdning…</p>
            )}
          </div>
        )}
      </div>
    );
  };

  // Visa inte om inga möten
  if (!loading && filteredMeetings.length === 0) {
    return null;
  }

  return (
    <div>
      {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Förslag som väntar på svar */}
              {pendingProposals.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-orange-600 mb-2 flex items-center">
                    <span className="w-2 h-2 bg-orange-500 rounded-full mr-2 animate-pulse"></span>
                    Väntar på ditt svar
                  </h3>
                  {pendingProposals.map(meeting => (
                    <div key={meeting._id} className="border border-orange-200 rounded-lg p-4 bg-orange-50/50 mb-3 last:mb-0">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="font-medium text-gray-900">
                            {meeting.displayName || getMeetingDisplayName(meeting)}
                          </h4>
                          <p className="text-sm text-gray-500">
                            {meeting.location?.displayName} - {formatDuration(meeting.duration)}
                          </p>
                        </div>
                        {renderHemlaxaChip(meeting)}
                      </div>

                      {respondingTo === meeting._id ? (
                        // Visa svarsformulär
                        <div className="space-y-3">
                          <p className="text-sm text-gray-600">Markera vilka tider som passar dig:</p>
                          {meeting.proposalWorkflow.proposals.map((proposal, idx) => (
                            <div key={idx} className="flex flex-wrap items-center justify-between gap-2 py-2 px-3 bg-white rounded-lg border">
                              <span className="text-sm font-medium min-w-0">
                                {proposal.displayText || formatMeetingDateTime(proposal.date, proposal.time)}
                              </span>
                              <div className="flex gap-2 flex-shrink-0">
                                <button
                                  onClick={() => handleResponseChange(idx, true)}
                                  className={`px-3 py-2 rounded text-sm ${
                                    responses[idx] === true
                                      ? 'bg-green-500 text-white'
                                      : 'bg-gray-100 text-gray-700 hover:bg-green-100'
                                  }`}
                                >
                                  Kan
                                </button>
                                <button
                                  onClick={() => handleResponseChange(idx, false)}
                                  className={`px-3 py-2 rounded text-sm ${
                                    responses[idx] === false
                                      ? 'bg-red-500 text-white'
                                      : 'bg-gray-100 text-gray-700 hover:bg-red-100'
                                  }`}
                                >
                                  Kan inte
                                </button>
                              </div>
                            </div>
                          ))}
                          <div className="flex gap-2 pt-2">
                            <button
                              onClick={() => handleSubmitResponse(meeting)}
                              disabled={submitting || Object.keys(responses).length !== meeting.proposalWorkflow.proposals.length}
                              className="flex-1 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                            >
                              {submitting ? 'Skickar...' : 'Skicka svar'}
                            </button>
                            <button
                              onClick={() => {
                                setRespondingTo(null);
                                setResponses({});
                              }}
                              className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm"
                            >
                              Avbryt
                            </button>
                          </div>
                        </div>
                      ) : (
                        // Visa föreslagna tider och svara-knapp
                        <div>
                          <p className="text-sm text-gray-600 mb-2">Föreslagna tider:</p>
                          <ul className="text-sm text-gray-700 space-y-1 mb-3">
                            {meeting.proposalWorkflow.proposals.map((proposal, idx) => (
                              <li key={idx} className="flex items-center">
                                <span className="w-1.5 h-1.5 bg-orange-400 rounded-full mr-2"></span>
                                {proposal.displayText || formatMeetingDateTime(proposal.date, proposal.time)}
                              </li>
                            ))}
                          </ul>
                          <button
                            onClick={() => {
                              setRespondingTo(meeting._id);
                              setResponses({});
                            }}
                            className="w-full py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 text-sm font-medium"
                          >
                            Svara på tidsförslag
                          </button>

                        </div>
                      )}

                      {meeting.type === 'hemlaxa' && (
                        <div className="mt-3 pt-3 border-t border-orange-200">
                          {renderHemlaxaUppladdning(meeting, { visaHjalptext: true })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Möten där kunden redan svarat (väntar på bokning) */}
              {respondedProposals.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-green-600 mb-2 flex items-center">
                    <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    Du har svarat - väntar på bokning
                  </h3>
                  {respondedProposals.map(meeting => {
                    const attendee = getCustomerAttendee(meeting);
                    return (
                      <div key={meeting._id} className="border border-green-200 rounded-lg p-4 bg-green-50/50 mb-2">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h4 className="font-medium text-gray-900">
                              {meeting.displayName || getMeetingDisplayName(meeting)}
                            </h4>
                            <p className="text-sm text-gray-500">
                              {meeting.location?.displayName} - {formatDuration(meeting.duration)}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {renderHemlaxaChip(meeting)}
                            <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
                              Svarat
                            </span>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">Dina svar:</p>
                        <div className="space-y-1">
                          {meeting.proposalWorkflow.proposals.map((proposal, idx) => {
                            const response = attendee?.proposalResponses?.find(r => r.proposalIndex === idx);
                            return (
                              <div key={idx} className="flex items-center text-sm">
                                {response?.canAttend ? (
                                  <span className="text-green-600 flex items-center">
                                    <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                    <span className="font-medium">Kan</span>
                                    <span className="text-gray-500 ml-2">{proposal.displayText || formatMeetingDateTime(proposal.date, proposal.time)}</span>
                                  </span>
                                ) : (
                                  <span className="text-red-500 flex items-center">
                                    <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                    <span className="font-medium">Kan inte</span>
                                    <span className="text-gray-500 ml-2">{proposal.displayText || formatMeetingDateTime(proposal.date, proposal.time)}</span>
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>

                        {meeting.type === 'hemlaxa' && (
                          <div className="mt-3 pt-3 border-t border-green-200">
                            {renderHemlaxaUppladdning(meeting, { visaHjalptext: true })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Kommande möten */}
              {upcomingMeetings.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-green-600 mb-2">Kommande möten</h3>
                  <div className="space-y-2">
                    {upcomingMeetings.map(meeting => {
                      const isExpanded = expandedMeetings[meeting._id];
                      const protocol = getProtocolForMeeting(meeting._id);
                      return (
                        <div key={meeting._id} className="border border-green-200 rounded-lg bg-green-50/50 overflow-hidden">
                          {/* Kompakt header - alltid synlig */}
                          <button
                            onClick={() => toggleMeetingExpanded(meeting._id)}
                            className="w-full px-3 py-2.5 flex items-center justify-between hover:bg-green-100/50 transition-colors"
                          >
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 min-w-0">
                              <span className="font-medium text-gray-900">
                                {meeting.displayName || getMeetingDisplayName(meeting)}
                              </span>
                              <span className="text-sm text-green-700 font-medium whitespace-nowrap">
                                {formatScheduledAt(meeting.scheduledAt)}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded">
                                Bokat
                              </span>
                              {renderHemlaxaChip(meeting)}
                              {protocol && (
                                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                                  Protokoll
                                </span>
                              )}
                              <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                            </div>
                          </button>

                          {/* Expanderat innehåll */}
                          {isExpanded && (
                            <div className="px-3 pb-3 pt-1 border-t border-green-200 bg-white/50">
                              {/* Adress / Teams-länk */}
                              {(meeting.location?.displayName || meeting.location?.address) && (
                                <div className="flex items-start gap-2 text-sm text-gray-600 mb-2">
                                  <MapPinIcon className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                                  {meeting.teamsJoinUrl ? (
                                    <a href={meeting.teamsJoinUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 hover:underline">
                                      Anslut via Microsoft Teams
                                    </a>
                                  ) : (
                                    <span>
                                      {meeting.location?.displayName || meeting.location?.address}
                                    </span>
                                  )}
                                </div>
                              )}

                              {/* Hemläxa-uppladdning (för hemläxa-möten) */}
                              {meeting.type === 'hemlaxa' && (
                                <div className="mb-2">
                                  {renderHemlaxaUppladdning(meeting)}
                                </div>
                              )}

                              {/* Protokoll om det finns */}
                              {protocol && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setProtocolModalMeeting(meeting);
                                  }}
                                  className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-blue-700 bg-blue-100 rounded hover:bg-blue-200 transition-colors"
                                >
                                  <DocumentTextIcon className="w-3.5 h-3.5" />
                                  Visa protokoll
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Tidigare möten (collapsed by default) */}
              {pastMeetings.length > 0 && (
                <details className="group">
                  <summary className="text-sm text-gray-500 cursor-pointer hover:text-gray-700 list-none flex items-center">
                    <svg className="w-4 h-4 mr-1 transform group-open:rotate-90 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                    Tidigare möten ({pastMeetings.length})
                    {antalSaknadHemlaxa > 0 && (
                      <span className="ml-2 px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-medium rounded">
                        Hemläxa saknas
                      </span>
                    )}
                  </summary>
                  <div className="mt-2 space-y-2">
                    {tidigareAttVisa.map(meeting => {
                      const protocol = getProtocolForMeeting(meeting._id);
                      const isExpanded = expandedMeetings[meeting._id];
                      return (
                        <div key={meeting._id} className="border rounded-lg bg-gray-50 overflow-hidden">
                          {/* Kompakt header */}
                          <button
                            onClick={() => toggleMeetingExpanded(meeting._id)}
                            className="w-full px-3 py-2 flex items-center justify-between hover:bg-gray-100 transition-colors"
                          >
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 min-w-0">
                              <span className="font-medium text-gray-700 text-sm">
                                {meeting.displayName || getMeetingDisplayName(meeting)}
                              </span>
                              <span className="text-xs text-gray-500 whitespace-nowrap">
                                {formatScheduledAt(meeting.scheduledAt)}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {renderHemlaxaChip(meeting)}
                              {protocol && (
                                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                                  Protokoll
                                </span>
                              )}
                              <span className="px-2 py-0.5 bg-gray-200 text-gray-600 text-xs rounded">
                                Genomfört
                              </span>
                              <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                            </div>
                          </button>

                          {/* Expanderat innehåll */}
                          {isExpanded && (
                            <div className="px-3 pb-3 pt-1 border-t border-gray-200 bg-white/50">
                              {/* Adress / Teams-länk */}
                              {(meeting.location?.displayName || meeting.location?.address) && (
                                <div className="flex items-start gap-2 text-sm text-gray-600 mb-2">
                                  <MapPinIcon className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                                  {meeting.teamsJoinUrl ? (
                                    <a href={meeting.teamsJoinUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 hover:underline">
                                      Anslut via Microsoft Teams
                                    </a>
                                  ) : (
                                    <span>
                                      {meeting.location?.displayName || meeting.location?.address}
                                    </span>
                                  )}
                                </div>
                              )}

                              {/* Hemläxa — knappen ligger kvar efter mötet och
                                  försvinner först när en fil är uppladdad. */}
                              {meeting.type === 'hemlaxa' && (
                                <div className="mb-2">
                                  {renderHemlaxaUppladdning(meeting)}
                                </div>
                              )}

                              {/* Protokoll */}
                              {protocol ? (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setProtocolModalMeeting(meeting);
                                  }}
                                  className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-blue-700 bg-blue-100 rounded hover:bg-blue-200 transition-colors"
                                >
                                  <DocumentTextIcon className="w-3.5 h-3.5" />
                                  Visa protokoll
                                </button>
                              ) : (
                                <span className="text-xs text-gray-400">Inget protokoll</span>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {(finnsDoldaTidigare || visaAllaTidigare) && (
                      <button
                        onClick={() => setVisaAllaTidigare(v => !v)}
                        className="w-full py-2 text-xs text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        {visaAllaTidigare
                          ? 'Visa färre'
                          : `Visa alla (${pastMeetings.length})`}
                      </button>
                    )}
                  </div>
                </details>
              )}
            </div>
          )}

      {/* Protokoll-modal (samma som BK ser) */}
      {protocolModalMeeting && (
        <ProtocolBuilder
          meetingId={protocolModalMeeting._id}
          meeting={protocolModalMeeting}
          onClose={() => setProtocolModalMeeting(null)}
        />
      )}
    </div>
  );
};

export default CustomerMeetingsSection;
